import useMediaQuery from 'hooks/useMediaQuery'
import { OrderProps } from 'hooks/useOrderB2c'
import { useCartProgressiveDiscount, UseCartProgressiveDiscountResult } from 'hooks/useCartProgressiveDiscount'
import { CartProgressiveLine } from 'components/ProgressiveDiscount/CartProgressiveLine'
import { MOBILE_WIDTH } from 'utils/constants'
import { getPayableAmount } from 'utils/reshipping'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import * as Style from './styles'

interface Props {
  order: OrderProps
  progressiveData?: UseCartProgressiveDiscountResult
}

function formatEstimateDate(iso: string | null | undefined) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const Resume = ({ order, progressiveData }: Props) => {
  const internal = useCartProgressiveDiscount(progressiveData ? [] : order?.items ?? [])
  const { preview, loading, error, hasEditedPrices, hint } = progressiveData ?? internal

  const frete = order?.shipping && Number(order?.shipping) > 0 ? Number(order?.shipping) : 145
  const items = order?.items.reduce(
    (acc, item) => {
      const value = item?.imputed_value !== '0.00' ? item?.imputed_value : item?.unitPrice
      const price =
        value !== null && value !== undefined && Number(value) >= 0 ? Number(value) : Number(item?.unitPrice)

      acc.subtotal += price * item.quantity
      // acc.total = acc.subtotal + frete - Number(acc.discount)

      return acc
    },
    {
      total: order.amount,
      subtotal: 0,
      discount: order?.discount,
      shippingDiscount: order?.shippingDiscount,
      totalDiscount: Number(order?.discount) + Number(order?.shippingDiscount),
      productDiscount: 0,
    }
  )

  const progressiveFromOrder = (order?.progressiveDiscountApplications ?? []).reduce(
    (sum, a) => sum + Number(a.total_discount),
    0,
  )
  const progressiveLineVisible = !hasEditedPrices && !error && !!preview && preview.total_progressive_discount !== 0
  const adjustedDiscount =
    Math.max(0, Number(order?.discount) - (progressiveLineVisible ? progressiveFromOrder : 0)) +
    Number(order?.shippingDiscount)

  const payableAmount = getPayableAmount(order)

  const isMobile = useMediaQuery(MOBILE_WIDTH)

  const hasRange = Boolean(order?.estimated_delivery_range_start && order?.estimated_delivery_range_end)

  return (
    <Style.Container isMobile={isMobile}>
      <Style.Title>Resumo do Pedido</Style.Title>
      {hasRange && (
        <Style.DeliveryEstimate>
          <strong>Estimativa de entrega</strong>
          <span>
            Entre {formatEstimateDate(order.estimated_delivery_range_start)} e{' '}
            {formatEstimateDate(order.estimated_delivery_range_end)}.
          </span>
        </Style.DeliveryEstimate>
      )}
      <Style.Prices>
        <Style.LeftContainer>
          {!items.subtotal ? <Skeleton style={{ width: '100px' }} /> : <span>Subtotal</span>}
          {!order.shipping ? <Skeleton style={{ width: '100px' }} /> : <span>Custo de importação</span>}
          {!items.discount ? <Skeleton style={{ width: '100px' }} /> : <span>Descontos</span>}
          <CartProgressiveLine
            preview={preview}
            loading={loading}
            error={error}
            hasEditedPrices={hasEditedPrices}
            side="label"
          />
        </Style.LeftContainer>
        <Style.RightContainer>
          {!items.subtotal ? (
            <Skeleton style={{ width: '100px' }} />
          ) : (
            <span>
              {new Intl.NumberFormat('pt-br', {
                style: 'currency',
                currency: 'BRL',
                currencyDisplay: 'symbol',
              }).format(items.subtotal)}
            </span>
          )}
          {!order.shipping ? (
            <Skeleton style={{ width: '100px' }} />
          ) : (
            <span>
              {new Intl.NumberFormat('pt-br', {
                style: 'currency',
                currency: 'BRL',
                currencyDisplay: 'symbol',
              }).format(frete)}
            </span>
          )}
          {!items.discount || !items.shippingDiscount ? (
            <Skeleton style={{ width: '100px' }} />
          ) : (
            <span>
              -{' '}
              {new Intl.NumberFormat('pt-br', {
                style: 'currency',
                currency: 'BRL',
                currencyDisplay: 'symbol',
              }).format(adjustedDiscount)}
            </span>
          )}
          <CartProgressiveLine
            preview={preview}
            loading={loading}
            error={error}
            hasEditedPrices={hasEditedPrices}
            side="value"
          />
        </Style.RightContainer>
      </Style.Prices>
      <CartProgressiveLine
        preview={preview}
        loading={loading}
        error={error}
        hasEditedPrices={hasEditedPrices}
        side="notice"
      />
      <CartProgressiveLine
        preview={preview}
        loading={loading}
        error={error}
        hasEditedPrices={hasEditedPrices}
        hint={hint}
        side="hint"
      />
      <hr />
      <Style.Total>
        {!items.total ? <Skeleton style={{ width: '100px' }} /> : <span className="total-title">Total</span>}
        {!items.total ? (
          <Skeleton style={{ width: '100px' }} />
        ) : (
          <span className="total">
            {new Intl.NumberFormat('pt-br', {
              style: 'currency',
              currency: 'BRL',
              currencyDisplay: 'symbol',
            }).format(payableAmount)}
          </span>
        )}
      </Style.Total>
    </Style.Container>
  )
}
